/* RESPONSIBLE TEAM: team-automation-foundations */
import Service, { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import type Model from '@ember-data/model';
import type Template from 'embercom/models/template';
import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';
import Ruleset from 'embercom/models/matching-system/ruleset';
import {
  CUSTOM_BOT_CONFIG,
  type BotConfigTarget,
} from 'embercom/objects/operator/configuration/configuration';
import { objectTypes } from 'embercom/models/data/matching-system/matching-constants';
import {
  type AllChannels,
  FIN_WORKFLOWS,
  NEW_CONVERSATION_TARGET,
} from 'embercom/lib/operator/custom-bots/constants';
import type IntlService from 'ember-intl/services/intl';
import { captureException } from 'embercom/lib/sentry';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';

export const FIN_WORKFLOW_TEMPLATE_IDS_PER_CHANNEL: Record<ChatChannel, string> = {
  messenger: '2014',
  slack: '2025',
  sms: '2026',
  whatsapp: '2029',
  facebook: '2016',
  instagram: '2030',
};

export const FIN_EMAIL_WORKFLOW_TEMPLATE_ID = '2017';

interface PreviewObject {
  save: () => Promise<any[] | Model>;
}

type Channel = Extract<
  AllChannels,
  'web' | 'ios' | 'android' | 'facebook' | 'whatsapp' | 'instagram' | 'sms' | 'slack' | 'email'
>;

export type ChatChannel =
  | 'messenger'
  | Extract<AllChannels, 'facebook' | 'whatsapp' | 'instagram' | 'sms' | 'slack'>;

export interface SetupArgs {
  ruleset: any;
  previewObject?: PreviewObject;
  onEdit?: () => void;
  onSetLive?: () => void;
}

export default class WorkflowCreationService extends Service {
  @service declare appService: AppService;
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare permissionsService: any;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare aiAgentSetupService: AiAgentSetupService;

  checkAdminPermission() {
    if (!this.permissionsService.currentAdminCan('can_create_and_edit_bots')) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_create_and_edit_bots',
      );
    }
  }

  checkBillingPermission() {
    if (!this.appService.app.canUseFeature('workflows_core_billing_feature')) {
      throw new Error('Workspace does not have access to workflows');
    }
  }

  async createWorkflowFromTemplate(template: Template) {
    this.checkAdminPermission();
    this.checkBillingPermission();

    let rulesetParams: {
      app_id: string;
      object_type: string | null;
      template_id?: string;
      role_predicate_group?: object;
      object_data?: object;
      match_behavior?: number;
    } = {
      app_id: this.appService.app.id,
      object_type: template.objectType,
    };
    let selectedTarget = template.ruleset?.rulesetLinks?.firstObject?.object?.target;
    let botConfig = selectedTarget && CUSTOM_BOT_CONFIG[selectedTarget as BotConfigTarget];
    let { matchBehavior, rolePredicateGroup, botType, trigger } = botConfig;

    if (template.id) {
      rulesetParams.template_id = template.id;
    } else {
      rulesetParams.match_behavior = matchBehavior;
      rulesetParams.role_predicate_group = rolePredicateGroup;
      rulesetParams.object_data = {
        type: botType,
        trigger_type: trigger,
      };
    }

    try {
      let ruleset = await Ruleset.createForType(this.store, rulesetParams);

      this.aiAgentSetupService.reloadFinWorkflowsCount();

      this.router.transitionTo('apps.app.automation.workflows.edit', ruleset, {
        queryParams: {
          mode: 'edit',
          returnPath: this.router.currentRouteName,
        },
      });
    } catch (error) {
      this.notifyError(error);
    }
  }

  async createWorkflowFromScratch({
    target = NEW_CONVERSATION_TARGET,
    targetChannels = undefined,
  }: Partial<{
    target: BotConfigTarget;
    targetChannels?: Channel[];
  }>) {
    this.checkAdminPermission();
    this.checkBillingPermission();

    let botConfig = CUSTOM_BOT_CONFIG[target];
    let { matchBehavior, rolePredicateGroup, botType, trigger } = botConfig;

    // Convert targetChannels to preferredDevices (web=0, ios=1, android=2)
    let preferredDevicesMap: Record<string, number> = {
      web: 0,
      ios: 1,
      android: 2,
    };

    let params = {
      app_id: this.appService.app.id,
      object_type: objectTypes.triggerableCustomBot,
      match_behavior: matchBehavior,
      role_predicate_group: rolePredicateGroup,
      object_data: {
        type: botType,
        trigger_type: trigger,
        ...(targetChannels && { target_channels: targetChannels }),
        ...(targetChannels && {
          preferred_devices: targetChannels
            .map((channel) => preferredDevicesMap[channel])
            .filter((device) => device !== undefined),
        }),
      },
    };

    try {
      let ruleset = await Ruleset.createForType(this.store, params);

      this.router.transitionTo('apps.app.automation.workflows.edit', ruleset, {
        queryParams: {
          mode: 'edit',
          returnPath: this.router.currentRouteName,
        },
      });
    } catch (error) {
      this.notifyError(error);
    }
  }

  notifyError(error: Error) {
    this.notificationsService.notifyResponseError(error, {
      default: this.intl.t(
        'operator.custom-bot.editor.no-trigger-bot-selector.could-not-create-bot',
      ),
    });
  }

  private get finWorkflowTemplates(): Template[] {
    let allTemplates = this.store.peekAll('template');

    if (allTemplates.length === 0) {
      captureException(
        new Error(
          'No templates found. Please load the templates in the route before calling the service.',
        ),
        {
          tags: {
            section: 'workflow-creation-service',
            subsection: 'fin-workflow-templates',
          },
        },
      );
      return [];
    }

    return allTemplates.filterBy('objective', FIN_WORKFLOWS.id);
  }

  private findTemplateById(templateId: string): Template | undefined {
    return this.finWorkflowTemplates.find((template) => template.id === templateId);
  }

  get finWorkflowTemplateForChat(): Record<ChatChannel, Template | undefined> {
    return {
      messenger: this.findTemplateById(FIN_WORKFLOW_TEMPLATE_IDS_PER_CHANNEL.messenger),
      slack: this.findTemplateById(FIN_WORKFLOW_TEMPLATE_IDS_PER_CHANNEL.slack),
      sms: this.findTemplateById(FIN_WORKFLOW_TEMPLATE_IDS_PER_CHANNEL.sms),
      whatsapp: this.findTemplateById(FIN_WORKFLOW_TEMPLATE_IDS_PER_CHANNEL.whatsapp),
      facebook: this.findTemplateById(FIN_WORKFLOW_TEMPLATE_IDS_PER_CHANNEL.facebook),
      instagram: this.findTemplateById(FIN_WORKFLOW_TEMPLATE_IDS_PER_CHANNEL.instagram),
    };
  }

  get finWorkflowTemplateForEmail(): Template | undefined {
    return this.findTemplateById(FIN_EMAIL_WORKFLOW_TEMPLATE_ID);
  }
}

declare module '@ember/service' {
  interface Registry {
    workflowCreationService: WorkflowCreationService;
    'workflow-creation-service': WorkflowCreationService;
  }
}
